// src/components/lists/BookingsList/ClientsBookingsList.tsx
// Client-specific bookings list - displays bookings for a single client
// Unlike BookingsList, it excludes client details and surname (client is already known)

import React from 'react';
import { Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Event as EventIcon } from '@mui/icons-material';

import { getCustomIcon } from '../../../utils/customIcons';
// Import shared base components
import {
  ListContainer,
  ListRow,
  RowClickableArea,
  ItemNameSection,
  ItemIcon,
  ItemName,
  ActionButtons,
} from '../base/ListStyles';

// Import BeingsList styled components for reservations/pets display
import {
  ReservationsSection,
  ReservationsList,
  ReservationItem,
  PetCountDisplay,
  ReservationDate,
} from '../BeingsList/beingsListStyles';

import { useListState, useListUtils } from '../base/ListBase';

import { BookingsListProps } from './bookingsListTypes';
import {
  formatBookingDateRange,
  formatBookingPetTypeCounts,
} from './bookingsListUtils';

import {
  BeingsListPrimaryButton,
  BeingsListSecondaryButton,
} from '../../common/Buttons';

// Enhanced ListRow with hover state for pet count display
const EnhancedListRow = styled(ListRow)<{
  isOpen?: boolean;
  isEven?: boolean;
}>`
  &:hover {
    .pet-count-display {
      opacity: ${(props) => (props.isOpen ? 1 : 0.8)} !important;
    }
  }
`;

// Pet columns for proper alignment - icon | name | type
const PetColumnsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: '0.8rem',
}));

const PetIconColumn = styled('div')(({ theme }) => ({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  '@media (max-width: 600px)': {
    paddingLeft: '1rem',
  },
}));

const PetNameColumn = styled('div')({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

// Styled span for bold date range (matching clientSurname styling)
const BoldDateRange = styled('span')({
  fontWeight: 600,
});

const BookingNumberText = styled('span')({
  fontWeight: 700,
});

// Spacer below pets list to show blue background
const PetsListSpacer = styled(Box)({
  height: '12px',
});

const ClientsBookingsList: React.FC<BookingsListProps> = ({
  items,
  onEdit,
  onView,
  onDelete,
  onViewDetails,
  onCancel,
}) => {
  const { openItemId, setOpenItemId } = useListState();
  const { handleRowClick, handleNameClick } = useListUtils(
    openItemId,
    setOpenItemId
  );

  const handleEditClick = (id: number) => {
    onEdit!(id);
  };

  const handleViewClick = (id: number) => {
    onView!(id);
  };

  if (!items || items.length === 0) {
    return (
      <ListContainer>
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant="h4" color="text.secondary">
            No bookings found
          </Typography>
        </Box>
      </ListContainer>
    );
  }

  return (
    <ListContainer>
      {items.map((booking, index) => {
        const isOpen = openItemId === booking.id;
        const isEven = index % 2 === 0;
        const dateRange = formatBookingDateRange(booking);
        const petTypeCounts = formatBookingPetTypeCounts(booking);
        const bookingLabel = `${dateRange} - ${petTypeCounts}`;

        return (
          <EnhancedListRow
            key={booking.id}
            isOpen={isOpen}
            isEven={isEven}
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
            aria-label={bookingLabel}
            onClick={() => handleRowClick(booking.id)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleRowClick(booking.id);
              }
            }}
          >
            {/* Clickable area */}
            <RowClickableArea
              isOpen={isOpen}
              onClick={() => handleRowClick(booking.id)}
            />

            {/* Main row content - Bold date range with pet type counts (no brackets, no surname) */}
            <ItemNameSection
              isOpen={isOpen}
              onClick={
                isOpen
                  ? (e) => handleNameClick(booking.id, e)
                  : (e) => {
                      e.stopPropagation();
                      handleRowClick(booking.id);
                    }
              }
              role="button"
              aria-label={bookingLabel}
              tabIndex={0}
            >
              <ItemIcon isOpen={isOpen}>
                <EventIcon />
              </ItemIcon>
              <ItemName isOpen={isOpen}>
                <BookingNumberText>#{booking.id} - </BookingNumberText>
                <BoldDateRange>{dateRange}</BoldDateRange>
                <PetCountDisplay isOpen={isOpen} className="pet-count-display">
                  {petTypeCounts}
                </PetCountDisplay>
              </ItemName>
            </ItemNameSection>

            {/* Action Buttons */}
            {isOpen && (
              <ActionButtons>
                <BeingsListPrimaryButton
                  size="small"
                  onClick={() => handleViewClick(booking.id)}
                >
                  View
                </BeingsListPrimaryButton>
                <BeingsListSecondaryButton
                  size="small"
                  disabled={booking.convertedToBookingId != null}
                  onClick={() => handleEditClick(booking.id)}
                >
                  Edit
                </BeingsListSecondaryButton>
              </ActionButtons>
            )}

            {/* Pets Section - with isOpen prop for show/hide */}
            <ReservationsSection isOpen={isOpen}>
              <ReservationsList>
                {booking.pets && booking.pets.length > 0 ? (
                  booking.pets.map((pet, idx) => (
                    <ReservationItem key={idx}>
                      <PetColumnsWrapper>
                        <PetIconColumn>
                          {getCustomIcon(pet.petType, '2.2rem')}
                        </PetIconColumn>
                        <PetNameColumn>
                          <ReservationDate>{pet.petName}</ReservationDate>
                        </PetNameColumn>
                      </PetColumnsWrapper>
                    </ReservationItem>
                  ))
                ) : (
                  <ReservationItem>
                    <ReservationDate>No pets in this booking</ReservationDate>
                  </ReservationItem>
                )}
              </ReservationsList>
              {/* Spacer to show blue background below pets list */}
              <PetsListSpacer />
            </ReservationsSection>
          </EnhancedListRow>
        );
      })}
    </ListContainer>
  );
};

export default ClientsBookingsList;
